import { z } from "zod";
import { calendarDate } from "@brewbase/db/calendar-date";
import { parseDecimal } from "./numeric-text";
import { SCALAR_UNIT } from "./unit-system";
import { CURVE_FORMULA_FIELDS } from "./attribute-formulas/context";
const decimal = (label) => z
    .string()
    .trim()
    .refine((s) => parseDecimal(s) !== null, `${label} must be a number`);
/**
 * A curve version as entered: the line (area = slope · concentration +
 * intercept), its fit, its retention-time window and calibration date. Numbers
 * are decimal strings so NUMERIC keeps what was typed.
 */
export const calibrationCurveValuesSchema = z
    .object({
    slope: decimal("Slope"),
    intercept: decimal("Intercept"),
    r_squared: decimal("R²"),
    rt_min: decimal("RT from"),
    rt_max: decimal("RT to"),
    calibrated_at: calendarDate,
    notes: z.string().trim().max(2000).nullable().optional(),
})
    .superRefine((v, ctx) => {
    const slope = parseDecimal(v.slope);
    if (slope === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["slope"],
            message: "Slope cannot be 0",
        });
    }
    const r2 = parseDecimal(v.r_squared);
    if (r2 !== null && (r2 < 0 || r2 > 1)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["r_squared"],
            message: "R² must be between 0 and 1",
        });
    }
    const rtMin = parseDecimal(v.rt_min);
    const rtMax = parseDecimal(v.rt_max);
    if (rtMin !== null && rtMin < 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["rt_min"],
            message: "RT cannot be negative",
        });
    }
    if (rtMin !== null && rtMax !== null && rtMax <= rtMin) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["rt_max"],
            message: "RT to must be after RT from",
        });
    }
});
/** A new curve: the method and component it is for, plus its first version. */
export const calibrationCurveCreateSchema = z.object({
    test_template_id: z.number().int().positive(),
    analyte_code: z.string().min(1),
    values: calibrationCurveValuesSchema,
});
/** Recalibrating an existing curve: its next version. */
export const calibrationCurveVersionSchema = z.object({
    calibration_curve_id: z.number().int().positive(),
    values: calibrationCurveValuesSchema,
});
/** A stored curve version (pg NUMERIC as strings) as formulas read it. */
export function toCurveScopeEntry(v) {
    return {
        slope: Number(v.slope),
        intercept: Number(v.intercept),
        r_squared: v.r_squared === null ? null : Number(v.r_squared),
        rt_min: Number(v.rt_min),
        rt_max: Number(v.rt_max),
    };
}
/**
 * The `curves` formula variable: one object per compound with its curve's
 * numbers, e.g. `curves.putrescine.slope`. An unrecorded R² is left out, so a
 * formula reading it fails clearly instead of using 0.
 */
export function curvesScopeValue(curves) {
    const scalar = (value) => ({ type: "scalar", value, unit: SCALAR_UNIT });
    return {
        type: "object",
        value: Object.fromEntries(Object.entries(curves).map(([code, c]) => [
            code,
            {
                type: "object",
                value: Object.fromEntries(CURVE_FORMULA_FIELDS.flatMap((f) => (c[f] === null ? [] : [[f, scalar(c[f])]]))),
            },
        ])),
    };
}
//# sourceMappingURL=calibration-curve.js.map